import type { ColumnPresetId } from "../column-presets";
import { applyRowColumnContentRebalance } from "../column-content-min";
import { findColumn, findGroup, findRow, locateRow } from "../find";
import { sanitizeFillColumnImagesInRow } from "../image-height";
import { deleteRow } from "./deletes";
import { isRow } from "../sections";
import { createBlock, createGroup, createRow } from "../factories";
import {
  applySpacerRowDefaults,
  canDeleteRow,
  clampSectionInsertIndex,
  isRowEmpty,
} from "../row-policy";
import { getSnippetById, type SnippetId } from "../snippets";
import type {
  BlockType,
  Block,
  DocumentSection,
  Group,
  Row,
} from "../types";
import type { EditorDraft, RowInsertTarget } from "./types";
import { moveContiguousBlocksToSlot } from "./bulk-block-operations";
import { applySelectionChange } from "./draft";
import {
  applySectionSelection,
  resolveTopLevelInsertIndex,
} from "./shared";

function spliceRowAt(
  draft: EditorDraft,
  target: RowInsertTarget,
  row: Row,
): boolean {
  if (typeof target === 'number') {
    const insertAt = clampSectionInsertIndex(draft.document.sections, target);
    draft.document.sections.splice(insertAt, 0, row);
    return true;
  }
  const group = findGroup(draft.document.sections, target.groupId);
  if (!group) {
    return false;
  }
  const insertAt = Math.max(0, Math.min(target.rowIndex, group.rows.length));
  group.rows.splice(insertAt, 0, row);
  return true;
}

function adjustRowInsertTarget(
  sections: DocumentSection[],
  sourceRowId: string,
  target: RowInsertTarget,
  willPruneSource: boolean,
): RowInsertTarget {
  if (!willPruneSource) {
    return target;
  }
  const located = locateRow(sections, sourceRowId);
  if (!located) {
    return target;
  }
  if (typeof target === 'number') {
    if (
      located.location.parent === 'document' &&
      located.location.sectionIndex < target
    ) {
      return target - 1;
    }
    return target;
  }
  if (
    located.location.parent === 'group' &&
    located.location.groupId === target.groupId &&
    located.location.rowIndex < target.rowIndex
  ) {
    return { ...target, rowIndex: target.rowIndex - 1 };
  }
  return target;
}

export function insertRow(
  draft: EditorDraft,
  target: RowInsertTarget,
  presetId: ColumnPresetId,
): boolean {
  const nestedInGroup = typeof target !== 'number';
  const row = createRow(presetId, undefined, { nestedInGroup });
  if (!spliceRowAt(draft, target, row)) {
    return false;
  }
  applySelectionChange(draft, { kind: 'row', rowId: row.id });
  return true;
}

function insertRowWithExistingBlock(
  draft: EditorDraft,
  target: RowInsertTarget,
  block: Block,
): boolean {
  if (!insertRow(draft, target, 'one')) {
    return false;
  }
  if (draft.selection.kind !== 'row') {
    return false;
  }
  const rowId = draft.selection.rowId;
  const row = findRow(draft.document, rowId);
  const column = row?.columns[0];
  if (!row || !column) {
    deleteRow(draft, rowId);
    return false;
  }
  if (block.type === 'spacer') {
    applySpacerRowDefaults(row);
  }
  column.blocks.push(block);
  sanitizeFillColumnImagesInRow(row);
  applyRowColumnContentRebalance(draft.document, rowId);
  applySelectionChange(draft, {
    kind: 'block',
    rowId,
    columnId: column.id,
    blockId: block.id,
  });
  return true;
}

export function insertBlock(
  draft: EditorDraft,
  rowId: string,
  columnId: string,
  index: number,
  blockType: BlockType,
): boolean {
  const column = findColumn(draft.document, rowId, columnId);
  if (!column) {
    return false;
  }
  const block = createBlock(blockType, draft.document.settings);
  column.blocks.splice(index, 0, block);
  const row = findRow(draft.document, rowId);
  if (row) {
    sanitizeFillColumnImagesInRow(row);
    applyRowColumnContentRebalance(draft.document, rowId);
  }
  applySelectionChange(draft, { kind: 'block', rowId, columnId, blockId: block.id });
  return true;
}

export function moveBlock(
  draft: EditorDraft,
  from: {
    rowId: string;
    columnId: string;
    blockId: string;
    blockIds?: string[];
  },
  to: { rowId: string; columnId: string; index: number },
): boolean {
  if (from.blockIds !== undefined && from.blockIds.length > 1) {
    return moveContiguousBlocksToSlot(draft, from.blockIds, to);
  }

  const fromColumn = findColumn(draft.document, from.rowId, from.columnId);
  const toColumn = findColumn(draft.document, to.rowId, to.columnId);
  if (!fromColumn || !toColumn) {
    return false;
  }
  const fromIndex = fromColumn.blocks.findIndex(
    block => block.id === from.blockId,
  );
  if (fromIndex < 0) {
    return false;
  }
  const [moved] = fromColumn.blocks.splice(fromIndex, 1);
  if (!moved) {
    return false;
  }
  let insertAt = to.index;
  if (
    from.rowId === to.rowId &&
    from.columnId === to.columnId &&
    fromIndex < insertAt
  ) {
    insertAt -= 1;
  }
  toColumn.blocks.splice(insertAt, 0, moved);
  const fromRow = findRow(draft.document, from.rowId);
  if (fromRow) {
    sanitizeFillColumnImagesInRow(fromRow);
    applyRowColumnContentRebalance(draft.document, from.rowId);
  }
  if (to.rowId !== from.rowId) {
    const toRow = findRow(draft.document, to.rowId);
    if (toRow) {
      sanitizeFillColumnImagesInRow(toRow);
      applyRowColumnContentRebalance(draft.document, to.rowId);
    }
  }
  applySelectionChange(draft, {
    kind: 'block',
    rowId: to.rowId,
    columnId: to.columnId,
    blockId: moved.id,
  });
  return true;
}

export function insertRowWithBlock(
  draft: EditorDraft,
  target: RowInsertTarget,
  blockType: BlockType,
): boolean {
  return insertRowWithExistingBlock(
    draft,
    target,
    createBlock(blockType, draft.document.settings),
  );
}

export function moveBlockToNewRow(
  draft: EditorDraft,
  from: { rowId: string; columnId: string; blockId: string },
  target: RowInsertTarget,
): boolean {
  const fromColumn = findColumn(draft.document, from.rowId, from.columnId);
  if (!fromColumn) {
    return false;
  }
  const fromIndex = fromColumn.blocks.findIndex(
    block => block.id === from.blockId,
  );
  if (fromIndex < 0) {
    return false;
  }
  const [moved] = fromColumn.blocks.splice(fromIndex, 1);
  if (!moved) {
    return false;
  }

  const fromRow = findRow(draft.document, from.rowId);
  if (fromRow) {
    sanitizeFillColumnImagesInRow(fromRow);
  }

  const willPrune = fromRow
    ? isRowEmpty(fromRow) && canDeleteRow(draft.document, from.rowId)
    : false;
  const adjustedTarget = adjustRowInsertTarget(
    draft.document.sections,
    from.rowId,
    target,
    willPrune,
  );

  if (!insertRowWithExistingBlock(draft, adjustedTarget, moved)) {
    fromColumn.blocks.splice(fromIndex, 0, moved);
    return false;
  }

  const blockSelection = draft.selection;

  if (willPrune) {
    deleteRow(draft, from.rowId);
  }

  if (blockSelection.kind === 'block') {
    applySelectionChange(draft, blockSelection);
  }

  return true;
}

export function insertGroup(
  draft: EditorDraft,
  index: number,
  group?: Group,
): boolean {
  const nextGroup = group ?? createGroup();
  const insertAt = clampSectionInsertIndex(draft.document.sections, index);
  draft.document.sections.splice(insertAt, 0, nextGroup);
  applySelectionChange(draft, { kind: 'group', groupId: nextGroup.id });
  return true;
}

export function insertSnippet(
  draft: EditorDraft,
  snippetId: SnippetId,
  index?: number,
): boolean {
  const snippet = getSnippetById(snippetId);
  if (!snippet) {
    return false;
  }

  const built = snippet.build();
  const insertAt = resolveTopLevelInsertIndex(draft, index);

  if (built.length === 0) {
    return false;
  }

  if (built.length === 1) {
    const [section] = built;
    if (!section) {
      return false;
    }
    draft.document.sections.splice(insertAt, 0, section);
    applySectionSelection(draft, section);
    return true;
  }

  const rows = built.filter(isRow);
  if (rows.length !== built.length) {
    return false;
  }

  const group = createGroup(rows);
  draft.document.sections.splice(insertAt, 0, group);
  applySelectionChange(draft, { kind: 'group', groupId: group.id });
  return true;
}
